import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Check, CreditCard, Info, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Spinner } from 'components/ui/spinner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import { formatNumber } from 'helpers';
import Locales from 'locales';
import { ECOBANK_PAYMENT_STATE } from './ecobankPaymentState';

const ECOBANK_LOCALES = Locales.invoice.ecobank;

const StateIcon = ({ state }) => {
  const styles = {
    [ECOBANK_PAYMENT_STATE.CONFIRMED]: 'bg-[#e8f5ed] text-[#18834b]',
    [ECOBANK_PAYMENT_STATE.REJECTED]: 'bg-[#fbefeb] text-[#b45a43]',
    [ECOBANK_PAYMENT_STATE.INDETERMINATE]: 'bg-[#eef3f7] text-[#566f82]',
    [ECOBANK_PAYMENT_STATE.INITIATION_FAILED]: 'bg-[#eef3f7] text-[#566f82]',
  };
  const className = `mb-4 grid size-[46px] place-items-center rounded-xl ${styles[state]}`;

  if (state === ECOBANK_PAYMENT_STATE.CONFIRMED) {
    return (
      <div className={className} aria-hidden="true">
        <Check className="size-6" />
      </div>
    );
  }

  if (state === ECOBANK_PAYMENT_STATE.REJECTED) {
    return (
      <div className={className} aria-hidden="true">
        <X className="size-6" />
      </div>
    );
  }

  return (
    <div className={className} aria-hidden="true">
      <Info className="size-6" />
    </div>
  );
};

StateIcon.propTypes = {
  state: PropTypes.oneOf(Object.values(ECOBANK_PAYMENT_STATE)).isRequired,
};

const TransactionSummary = ({ invoice }) => (
  <div className="mt-5 flex flex-col gap-2 border-t border-[#e6ebe7] pt-4 text-sm text-[#2f3e35] sm:flex-row sm:items-center sm:justify-between sm:gap-4">
    <span>{`${invoice.code} · ${invoice.agency_name || '-'}`}</span>
    <strong className="whitespace-nowrap font-medium tabular-nums">{formatNumber(invoice.amount)} F CFA</strong>
  </div>
);

TransactionSummary.propTypes = {
  invoice: PropTypes.shape({
    code: PropTypes.string,
    agency_name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
};

const EcobankPaymentModal = ({
  invoice,
  isOpen,
  onOpenChange,
  paymentState,
  maskedAccount,
  safeRejectionReason,
  onPaymentStateChange,
}) => {
  const [viewState, setViewState] = useState(paymentState);
  const [authenticatorCode, setAuthenticatorCode] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    setViewState(paymentState);
    setAuthenticatorCode('');
  }, [invoice?.id, isOpen, paymentState]);

  useEffect(() => {
    if (isOpen && viewState === ECOBANK_PAYMENT_STATE.AUTHORIZATION_REQUIRED) inputRef.current?.focus();
  }, [isOpen, viewState]);

  if (!invoice) return null;

  const isInitiating = viewState === ECOBANK_PAYMENT_STATE.INITIATING;
  const isAuthorization = viewState === ECOBANK_PAYMENT_STATE.AUTHORIZATION_REQUIRED;
  const isValidating = viewState === ECOBANK_PAYMENT_STATE.VALIDATING;
  const isReview = viewState === ECOBANK_PAYMENT_STATE.FRESH;
  const pilgrimCount = invoice.trips?.length || invoice.deposit_count || 0;

  const closeModal = () => {
    if (isInitiating) return;
    setAuthenticatorCode('');
    onOpenChange(false);
  };

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) closeModal();
    else onOpenChange(true);
  };

  const continueToAuthorization = () => {
    setViewState(ECOBANK_PAYMENT_STATE.AUTHORIZATION_REQUIRED);
    onPaymentStateChange(ECOBANK_PAYMENT_STATE.AUTHORIZATION_REQUIRED);
  };

  const validatePayment = (event) => {
    event.preventDefault();
    if (!authenticatorCode.trim() || isValidating) return;
    setAuthenticatorCode('');
    setViewState(ECOBANK_PAYMENT_STATE.VALIDATING);
    onPaymentStateChange(ECOBANK_PAYMENT_STATE.VALIDATING);
  };

  const modalHeader = {
    [ECOBANK_PAYMENT_STATE.FRESH]: [ECOBANK_LOCALES.modal.review_title, ECOBANK_LOCALES.modal.review_description],
    [ECOBANK_PAYMENT_STATE.INITIATING]: [ECOBANK_LOCALES.modal.review_title, ECOBANK_LOCALES.modal.review_description],
    [ECOBANK_PAYMENT_STATE.INITIATION_FAILED]: [
      ECOBANK_LOCALES.modal.initiation_failed_title,
      `${ECOBANK_LOCALES.field.invoice} ${invoice.code}`,
    ],
    [ECOBANK_PAYMENT_STATE.AUTHORIZATION_REQUIRED]: [
      ECOBANK_LOCALES.modal.authorization_title,
      `${ECOBANK_LOCALES.field.invoice} ${invoice.code}`,
    ],
    [ECOBANK_PAYMENT_STATE.VALIDATING]: [
      ECOBANK_LOCALES.modal.authorization_title,
      `${ECOBANK_LOCALES.field.invoice} ${invoice.code}`,
    ],
    [ECOBANK_PAYMENT_STATE.CONFIRMED]: [
      ECOBANK_LOCALES.modal.confirmed_title,
      `${ECOBANK_LOCALES.field.invoice} ${invoice.code}`,
    ],
    [ECOBANK_PAYMENT_STATE.REJECTED]: [
      ECOBANK_LOCALES.modal.rejected_title,
      `${ECOBANK_LOCALES.field.invoice} ${invoice.code}`,
    ],
    [ECOBANK_PAYMENT_STATE.INDETERMINATE]: [
      ECOBANK_LOCALES.modal.indeterminate_title,
      `${ECOBANK_LOCALES.field.invoice} ${invoice.code}`,
    ],
  }[viewState];

  const renderReview = () => (
    <>
      <div className="space-y-5 px-6 py-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <span className="block text-sm text-[#6c7970]">{ECOBANK_LOCALES.field.invoice}</span>
            <strong className="text-sm font-medium text-[#2b3e32]">{invoice.code}</strong>
          </div>
          <div>
            <span className="block text-sm text-[#6c7970]">{ECOBANK_LOCALES.field.agency}</span>
            <strong className="text-sm font-medium text-[#2b3e32]">{invoice.agency_name || '-'}</strong>
          </div>
        </div>
        <div className="flex items-baseline justify-between gap-4">
          <span className="text-sm text-[#6c7970]">{ECOBANK_LOCALES.field.pilgrims}</span>
          <strong className="text-sm font-medium text-[#2b3e32]">{pilgrimCount}</strong>
        </div>
        <div className="rounded-lg border border-[#e1e7e3] bg-[#f7f9f8] px-5 py-4">
          <span className="block text-sm text-[#6c7970]">{ECOBANK_LOCALES.field.amount_due}</span>
          <strong className="mt-1 block text-[28px] font-semibold leading-9 tracking-[-0.02em] text-[#22382b]">
            {formatNumber(invoice.amount)} <span className="text-base font-medium text-[#56665b]">F CFA</span>
          </strong>
        </div>
        <div className="flex items-center gap-3 border-b border-[#e5e9e6] pb-5">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg border border-[#e0e7e2] bg-[#fafbfa] text-[#758178]">
            <CreditCard className="size-5" aria-hidden="true" />
          </span>
          <div>
            <span className="block text-sm text-[#6c7970]">{ECOBANK_LOCALES.field.account_due}</span>
            <strong className="font-medium tracking-[0.12em] text-[#3a4e41]">{maskedAccount}</strong>
          </div>
        </div>
        <p className="text-sm leading-6 text-[#6a766f]">{ECOBANK_LOCALES.message.review}</p>
        {isInitiating && (
          <div
            className="flex items-center gap-2 rounded-lg bg-[#f6f8fa] px-4 py-3 text-sm text-[#667582]"
            role="status"
          >
            <Spinner aria-label={ECOBANK_LOCALES.message.initializing} />
            {ECOBANK_LOCALES.message.initializing}
          </div>
        )}
      </div>
      <DialogFooter className="border-t border-[#e8ece9] bg-[#fcfdfc] px-6 py-4">
        <Button type="button" variant="outline" disabled={isInitiating} onClick={closeModal}>
          {ECOBANK_LOCALES.action.cancel}
        </Button>
        <Button type="button" variant="hajj" disabled={isInitiating} onClick={continueToAuthorization}>
          {isInitiating && <Spinner className="mr-1" aria-label={ECOBANK_LOCALES.message.initializing} />}
          {isInitiating ? ECOBANK_LOCALES.action.initializing : ECOBANK_LOCALES.action.next}
        </Button>
      </DialogFooter>
    </>
  );

  const renderAuthorization = () => (
    <form onSubmit={validatePayment}>
      <div className="space-y-5 px-6 py-6">
        <div className="flex flex-col gap-3 rounded-lg border border-[#e0e7e2] bg-[#f8faf9] px-4 py-3.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div>
            <span className="block text-sm text-[#6c7970]">{ECOBANK_LOCALES.field.amount}</span>
            <strong className="text-lg font-semibold tabular-nums text-[#22382b]">
              {formatNumber(invoice.amount)} F CFA
            </strong>
          </div>
          <div className="text-right">
            <span className="block text-sm text-[#6c7970]">{ECOBANK_LOCALES.field.account}</span>
            <strong className="font-medium tracking-[0.12em] text-[#3a4e41]">{maskedAccount}</strong>
          </div>
        </div>
        <p className="text-sm leading-6 text-[#6a766f]">{ECOBANK_LOCALES.message.authenticator}</p>
        <div>
          <label htmlFor="ecobank-authenticator-code" className="mb-2 block text-sm font-medium text-[#34483b]">
            {ECOBANK_LOCALES.field.authenticator_code}
          </label>
          <Input
            ref={inputRef}
            id="ecobank-authenticator-code"
            name="ecobank-authenticator-code"
            type="text"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck="false"
            value={authenticatorCode}
            disabled={isValidating}
            onChange={(event) => setAuthenticatorCode(event.target.value)}
            placeholder={ECOBANK_LOCALES.message.authenticator_placeholder}
            className="h-[46px]"
          />
        </div>
        {isValidating ? (
          <div
            className="flex items-center gap-2 rounded-lg bg-[#f6f8fa] px-4 py-3 text-sm text-[#667582]"
            role="status"
          >
            <Spinner aria-label={ECOBANK_LOCALES.message.validating} />
            {ECOBANK_LOCALES.message.validating}
          </div>
        ) : (
          <div className="flex items-start gap-2 text-sm leading-5 text-[#7a857f]">
            <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <span>{ECOBANK_LOCALES.message.resume_help}</span>
          </div>
        )}
      </div>
      <DialogFooter className="border-t border-[#e8ece9] bg-[#fcfdfc] px-6 py-4">
        <Button type="submit" variant="hajj" disabled={isValidating || !authenticatorCode.trim()}>
          {isValidating && <Spinner className="mr-1" aria-label={ECOBANK_LOCALES.message.validating} />}
          {isValidating ? ECOBANK_LOCALES.message.validating : ECOBANK_LOCALES.action.validate}
        </Button>
      </DialogFooter>
    </form>
  );

  const renderOutcome = () => {
    const outcome = {
      [ECOBANK_PAYMENT_STATE.INITIATION_FAILED]: {
        message: ECOBANK_LOCALES.message.initiation_failed,
        note: ECOBANK_LOCALES.message.initiation_retry,
      },
      [ECOBANK_PAYMENT_STATE.CONFIRMED]: {
        message: ECOBANK_LOCALES.message.confirmed,
      },
      [ECOBANK_PAYMENT_STATE.REJECTED]: {
        message: ECOBANK_LOCALES.message.rejected,
        note: ECOBANK_LOCALES.message.invoice_pending,
      },
      [ECOBANK_PAYMENT_STATE.INDETERMINATE]: {
        message: ECOBANK_LOCALES.message.indeterminate,
        note: ECOBANK_LOCALES.message.indeterminate_lock,
        pending: ECOBANK_LOCALES.message.invoice_pending,
      },
    }[viewState];

    return (
      <>
        <div className="px-6 py-6">
          <StateIcon state={viewState} />
          <p className="text-sm leading-6 text-[#6a766f]">{outcome.message}</p>
          {viewState === ECOBANK_PAYMENT_STATE.REJECTED && safeRejectionReason && (
            <div className="mt-4 rounded-lg border border-[#eee3df] bg-[#fbf7f5] px-4 py-3 text-sm text-[#6a5a54]">
              <span className="mb-1 block font-medium text-[#493b36]">{ECOBANK_LOCALES.field.reason}</span>
              {safeRejectionReason}
            </div>
          )}
          {outcome.note && (
            <div className="mt-4 rounded-lg bg-[#f6f8fa] px-4 py-3 text-sm leading-6 text-[#667582]">
              {outcome.note}
            </div>
          )}
          {outcome.pending && <p className="mt-3 text-sm text-[#6a766f]">{outcome.pending}</p>}
          <TransactionSummary invoice={invoice} />
        </div>
        <DialogFooter className="border-t border-[#e8ece9] bg-[#fcfdfc] px-6 py-4">
          <Button type="button" variant="hajj" onClick={closeModal}>
            {ECOBANK_LOCALES.action.close}
          </Button>
        </DialogFooter>
      </>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        closeDisabled={isInitiating}
        className="max-h-[calc(100dvh-1rem)] w-[calc(100%-1rem)] max-w-[512px] gap-0 overflow-y-auto rounded-xl border-[#e0e6e2] bg-white p-0 shadow-2xl sm:max-h-[calc(100dvh-2rem)]"
      >
        <DialogHeader className="px-6 pb-0 pt-6 pr-12 text-left">
          <DialogTitle className="text-xl font-semibold text-[#20342a]">{modalHeader[0]}</DialogTitle>
          <DialogDescription className="text-sm text-[#778279]">{modalHeader[1]}</DialogDescription>
        </DialogHeader>
        {(isReview || isInitiating) && renderReview()}
        {(isAuthorization || isValidating) && renderAuthorization()}
        {![
          ECOBANK_PAYMENT_STATE.FRESH,
          ECOBANK_PAYMENT_STATE.INITIATING,
          ECOBANK_PAYMENT_STATE.AUTHORIZATION_REQUIRED,
          ECOBANK_PAYMENT_STATE.VALIDATING,
        ].includes(viewState) && renderOutcome()}
      </DialogContent>
    </Dialog>
  );
};

EcobankPaymentModal.propTypes = {
  invoice: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    agency_name: PropTypes.string,
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    deposit_count: PropTypes.number,
    trips: PropTypes.arrayOf(PropTypes.object),
  }),
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  paymentState: PropTypes.oneOf(Object.values(ECOBANK_PAYMENT_STATE)).isRequired,
  maskedAccount: PropTypes.string.isRequired,
  safeRejectionReason: PropTypes.string,
  onPaymentStateChange: PropTypes.func.isRequired,
};

export default EcobankPaymentModal;
